import type { ComponentChildren } from "preact";
import type { JSX } from "preact/compat";

import { disableInput } from "../Utility";
import styles from "./Button.module.css";
import { clsx } from "./clsx";

export type ButtonVariant = "contained" | "outlined" | "text";
export type ButtonSize = "small" | "medium" | "large";
export type ButtonColor = "primary" | "secondary" | "purple" | "blue" | "orange" | "lightBlue" | "green" | "yellow";

export interface ButtonProps extends Omit<JSX.HTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  fullWidth?: boolean;
  startIcon?: ComponentChildren;
  endIcon?: ComponentChildren;
  children?: ComponentChildren;
  disabled?: boolean;
  component?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "contained",
  size = "medium",
  color,
  fullWidth = false,
  startIcon,
  endIcon,
  className,
  disabled,
  onClick,
  children,
  ...rest
}) => {
  const classes = clsx(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.fullWidth]: fullWidth,
      [styles.disabled]: disabled,
    },
    className as string
  );

  const colorAttribute = color ? { [`button-color-${color}`]: "" } : {};

  return (
    <button
      className={classes}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseDown={disableInput}
      onTouchStart={disableInput}
      {...colorAttribute}
      {...rest}
    >
      {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
    </button>
  );
};
